import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, unwrap } from "../../lib/api";
import { idempotencyHeaders, useApiMutation } from "../../lib/mutation";
import { Button } from "../../ui/Button";
import { Field } from "../../ui/Field";
import { Input } from "../../ui/Input";
import { toast } from "../../ui/toast";
import { OrganizationRow } from "./OrganizationRow";

const EMPTY = { name: "", slug: "", admin: { email: "", name: "", password: "" } };

/** Organizations of the instance and a new one with its first admin (instance administrators, #230). */
export function OrganizationsPage() {
  const { t } = useTranslation();
  const organizations = useQuery({
    queryKey: ["platform-organizations"],
    queryFn: () => unwrap(api.platform.organizations.$get()),
  });
  const [form, setForm] = useState(EMPTY);
  const setAdmin = (admin: Partial<typeof EMPTY.admin>) =>
    setForm({ ...form, admin: { ...form.admin, ...admin } });
  const create = useApiMutation(
    (_: undefined, key: string) =>
      unwrap(api.platform.organizations.$post({ json: form }, idempotencyHeaders(key))),
    {
      onSuccess: () => {
        toast(t("organizations.created"));
        setForm(EMPTY);
        void organizations.refetch();
      },
    },
  );
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.run();
  };

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 p-6">
      <div>
        <h1 className="text-lg font-semibold">{t("organizations.title")}</h1>
        <p className="text-sm text-zinc-500">{t("organizations.hint")}</p>
      </div>
      <ul className="divide-y divide-zinc-800 rounded-lg border border-zinc-800 text-sm">
        {organizations.data?.map((o) => (
          <OrganizationRow
            key={o.id}
            organization={o}
            onSaved={() => void organizations.refetch()}
          />
        ))}
      </ul>
      <form
        onSubmit={submit}
        className="grid grid-cols-1 gap-3 rounded-lg border border-zinc-800 p-4 sm:grid-cols-2"
      >
        <h2 className="text-sm font-semibold sm:col-span-2">{t("organizations.add")}</h2>
        <Field label={t("organizations.name")}>
          <Input
            required
            minLength={2}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label={t("organizations.slug")} hint={t("organizations.slugHint")}>
          <Input
            required
            pattern="[a-z0-9][a-z0-9.\-]{1,59}"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
          />
        </Field>
        <h3 className="text-xs font-semibold text-zinc-300 sm:col-span-2">
          {t("organizations.admin")}
          <span className="block font-normal text-zinc-500">{t("organizations.adminHint")}</span>
        </h3>
        <Field label={t("auth.email")}>
          <Input
            type="email"
            required
            value={form.admin.email}
            onChange={(e) => setAdmin({ email: e.target.value })}
          />
        </Field>
        <Field label={t("members.name")}>
          <Input
            required
            minLength={2}
            value={form.admin.name}
            onChange={(e) => setAdmin({ name: e.target.value })}
          />
        </Field>
        <Field label={t("members.tempPassword")} hint={t("members.tempPasswordHint")}>
          <Input
            type="text"
            required
            minLength={10}
            value={form.admin.password}
            onChange={(e) => setAdmin({ password: e.target.value })}
          />
        </Field>
        <Button
          type="submit"
          variant="primary"
          className="sm:col-span-2"
          disabled={create.isPending}
        >
          {t("organizations.add")}
        </Button>
      </form>
    </div>
  );
}
